// CaeraLogo.jsx — CAERA brand lockup (navy badge icon + Jost wordmark)
// Icon master: caera-icon.png — a rounded square, navy fill, copper "C" mark.
// Wrapped in a plain var(--caera-light-copper) rounded-square outline — no glow,
// just a border — so the mark reads as a contained badge on any surface.
// Usage:
//   <CaeraLogo size={28} darkBg />           ← sidebar (icon + wordmark)
//   <CaeraLogo size={28} showText={false} /> ← icon only
//   <CaeraIcon size={32} />                  ← icon only, direct

import React from 'react';
import caeraIcon from '../assets/brand/caera-icon.png';

// `chrome` = rendered on the app shell (sidebar, mobile sheet, portal topbar),
// where the outline has to follow the chrome shade: --caera-light-copper reads
// on navy but disappears on the light Parchment chrome.
export function CaeraIcon({ size = 28, chrome = false }) {
  return (
    <div
      style={{
        height: size,
        width: size,
        borderRadius: size * 0.22,
        border: `${Math.max(1, size * 0.045)}px solid ${chrome ? 'var(--ch-copper)' : 'var(--caera-light-copper)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      <img
        src={caeraIcon}
        alt="CAERA"
        style={{ height: '82%', width: '82%', display: 'block', objectFit: 'contain' }}
      />
    </div>
  );
}

// textRatio: fontSize as a multiple of icon `size`. Default (0.55) keeps the
// wordmark compact for nav contexts (sidebar/mobile menu, where the row has
// to fit a fixed-width column). Pass a bigger ratio (~1.4) for hero contexts
// like the login page, where the text should read at roughly the icon's own
// cap-height rather than a fraction of it.
export function CaeraLogo({ size = 28, showText = true, darkBg = true, textRatio = 0.55, chrome = false }) {
  if (!showText) return <CaeraIcon size={size} chrome={chrome} />;

  const fontSize = size * textRatio;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.32 }}>
      <CaeraIcon size={size} chrome={chrome} />
      <span
        style={{
          fontFamily: "'Jost', 'DM Sans', sans-serif",
          fontWeight: 600,
          fontSize,
          letterSpacing: '0.2em',
          color: chrome ? 'var(--ch-t1)' : darkBg ? 'var(--caera-ivory)' : 'var(--caera-ink)',
          textTransform: 'uppercase',
          lineHeight: 1,
          // wide tracking adds trailing space after the final A — nudge back
          marginRight: `-${fontSize * 0.2}px`,
        }}
      >
        CAERA
      </span>
    </div>
  );
}

export default CaeraLogo;
